import React from 'react';
import { useFormContext } from 'react-hook-form';
import { mapToOption } from '../../../../utlis';
import SearchableDropdown from './SearchableDropdown';
import { mapToManagerOptions } from './mapToManagerOptions';
import PhoneInput from './PhoneInput';

// Map users to dropdown options (value: user_id as string, text: user_name)
const mapToUserOptions = (users) => {
  if (!users || !Array.isArray(users)) return [{ value: '', text: '--Select Login ID--' }];

  const options = users.map((user) => {
    const userId = user.user_id;
    return {
      value: userId !== undefined && userId !== null ? String(userId) : '',
      text: user.user_name || user.username || user.userName ||
            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
    };
  });

  return [{ value: '', text: '--Select Login ID--' }, ...options];
};

const PersonalDetailsForm = ({ jobs, managers, refSalesLoading, users, usersLoading, apiFieldErrors, initialLoginId }) => {
  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();

  const jobTitle = watch('jobTitle');

  // Re-apply loginId after users list loads so the dropdown shows the correct value
  React.useEffect(() => {
    if (!usersLoading && users?.length > 0 && initialLoginId) {
      setValue('loginId', String(initialLoginId));
    }
  }, [usersLoading, users, initialLoginId, setValue]);
  const isManager = jobTitle?.toLowerCase() === 'manager';

  // Helper function to get error border class
  const getErrorBorderClass = (fieldName) => {
    return errors[fieldName] || apiFieldErrors?.[fieldName]
      ? 'border-danger-500'
      : 'border-secondary-500';
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
        <div className="flex flex-col w-full">
          <label htmlFor="firstName" className="text-text-hint mb-1">
            First Name<span className="text-danger-500">*</span>
          </label>
          <input
            {...register('firstName', { required: 'First name is required' })}
            type="text"
            placeholder="First Name"
            id="firstName"
            className={`outline-none border-2 ${getErrorBorderClass('firstName')} rounded-lg text-black pencialicon`}
          />
          {errors.firstName && <span className="text-danger-500">{errors.firstName.message}</span>}
        </div>
        <div className="flex flex-col w-full">
          <label htmlFor="middleName" className="text-text-hint mb-1">
            Middle Name
          </label>
          <input
            {...register('middleName')}
            type="text"
            placeholder="Middle Name"
            id="middleName"
            className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
          />
        </div>
        <div className="flex flex-col w-full">
          <label htmlFor="lastName" className="text-text-hint mb-1">
            Last Name<span className="text-danger-500">*</span>
          </label>
          <input
            {...register('lastName', { required: 'Last name is required' })}
            type="text"
            placeholder="Last Name"
            id="lastName"
            className={`outline-none border-2 ${getErrorBorderClass('lastName')} rounded-lg text-black pencialicon`}
          />
          {errors.lastName && <span className="text-danger-500">{errors.lastName.message}</span>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-5">
        <div className="flex flex-col w-full">
          <label htmlFor="preferredName" className="text-text-hint mb-1">
            Preferred Name
          </label>
          <input
            {...register('preferredName')}
            type="text"
            placeholder="Preferred Name"
            id="preferredName"
            className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
          />
        </div>
        <PhoneInput name="mobileNum" label="Mobile Number" required={true} hasApiError={!!apiFieldErrors?.mobileNum} />

        <div className="flex flex-col w-full">
          <label htmlFor="email" className="text-text-hint mb-1">
            Email<span className="text-danger-500">*</span>
          </label>
          <input
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Must be a valid email address',
              },
            })}
            type="email"
            placeholder="youremail@gmail.com"
            id="email"
            className={`outline-none border-2 ${getErrorBorderClass('email')} rounded-lg text-black pencialicon`}
          />
          {errors.email && <span className="text-danger-500">{errors.email.message}</span>}
        </div>

        <div className="flex flex-col w-full">
          <label htmlFor="jobTitle" className="text-text-hint mb-1">
            Job Title<span className="text-danger-500">*</span>
          </label>
          <select
            {...register('jobTitle', { required: 'Job title is required' })}
            id="jobTitle"
            className="outline-none border-2 border-secondary-500 rounded-lg text-black dropdown"
          >
            {mapToOption(jobs?.salesteam || [], 'jobtitle').map((option, index) => (
              <option key={`${option.value}-${index}`} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
          {errors.jobTitle && <span className="text-danger-500">{errors.jobTitle.message}</span>}
        </div>

        <div className="flex flex-col w-full max-w-md">
          <label htmlFor="reportingManager" className="text-text-hint mb-1">
            Reporting Manager {!isManager && <span className="text-danger-500">*</span>}
          </label>
          <SearchableDropdown
            options={mapToManagerOptions(managers || [])}
            value={getValues('reportingManager')}
            onChange={(val) => setValue('reportingManager', val, { shouldValidate: true, shouldDirty: true })}
            placeholder="Search or select manager"
            required={!isManager}
            name="reportingManager"
          />
          {errors.reportingManager && (
            <span className="text-danger-500">{errors.reportingManager.message}</span>
          )}
        </div>

        <div className="flex flex-col w-full">
          <label htmlFor="loginId" className="text-text-hint mb-1">
            Login ID
          </label>
          <select
            {...register('loginId')}
            id="loginId"
            className="outline-none border-2 border-secondary-500 rounded-lg text-black dropdown"
            disabled={usersLoading}
          >
            {usersLoading ? (
              <option value="">Loading users...</option>
            ) : (
              mapToUserOptions(users).map((option, index) => (
                <option key={`${option.value}-${index}`} value={option.value}>
                  {option.text}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
    </>
  );
};

export default PersonalDetailsForm;
